import { ZmanimData } from '../types';
import { CANDLE_LIGHTING_MINUTES_BEFORE_SUNSET, RABBEINU_TAM_MINUTES_AFTER_SUNSET } from '../config/constants';
import { applyOffset } from './timeUtils';
import { hebcalTzeit } from './hebcalSun';

/**
 * A time from MyZmanim's raw answer ("2026-11-07T17:24:00", sometimes with Z)
 * as "HH:mm"; undefined when missing or the "no time" value (year 0001).
 */
export function myZmanimTime(val: unknown): string | undefined {
  if (typeof val !== 'string' || val.startsWith('0001')) return undefined;
  const m = /T(\d{2}:\d{2})/.exec(val);
  return m ? m[1] : undefined;
}

/**
 * The app's own definitions, applied to every day's zmanim whatever their source
 * (MyZmanim cache or Hebcal), so both agree with the local calendar ("Itim LeBina")
 * within a minute (Yoel, November 4, 2026; before that Tzeit was sunset + 33):
 * - Sunset = from the height (MyZmanim's SunsetDefault; Hebcal with the neighborhood's height).
 * - Tzeit HaKochavim = Shabbos ends = MyZmanim's "NightShabbos"; when a saved day has
 *   none, Hebcal's sun 8.5 degrees below the horizon (the same within a minute).
 * - Tzeit Rabbeinu Tam = MyZmanim's "Night72fix" (sunset from the height + 72 minutes),
 *   otherwise sunset + 72.
 * - Candle lighting = sunset from the height - 40 minutes, on days that have candle
 *   lighting before sunset. Lighting after dark (the second night of Yom Tov,
 *   Yom Tov after Shabbos) is kept as the source gives it.
 */
export function normalizeZmanim(z: ZmanimData): ZmanimData {
  if (!z.SunsetDefault || z.SunsetDefault === '00:00') return z;
  const rawZman = (z.raw?.Zman ?? undefined) as Record<string, unknown> | undefined;
  const fromHebcal = z.locationId === 'hebcal-local';

  const tzet = fromHebcal
    ? z.Tzet
    : myZmanimTime(rawZman?.NightShabbos) ?? (z.date ? hebcalTzeit(z.date) : z.Tzet);
  const rabbeinuTam =
    (!fromHebcal && myZmanimTime(rawZman?.Night72fix)) ||
    applyOffset(z.SunsetDefault, RABBEINU_TAM_MINUTES_AFTER_SUNSET);
  const candle =
    z.candleLighting && z.candleLighting <= z.SunsetDefault
      ? applyOffset(z.SunsetDefault, -CANDLE_LIGHTING_MINUTES_BEFORE_SUNSET)
      : z.candleLighting;
  return {
    ...z,
    Tzet: tzet,
    TzetRabbeinuTam: rabbeinuTam,
    ...(candle ? { candleLighting: candle } : {}),
  };
}
